import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "lib/utils";

const cardVariants = cva("rounded-lg text-card-foreground", {
    variants: {
        variant: {
            default: "bg-card shadow-xl",
            muted: "bg-white shadow-none",
            detail: "rounded-2xl bg-white shadow-none",
            catalog:
                "flex h-full flex-col gap-4 rounded-3xl border-2 border-transparent bg-card p-5 shadow-none cursor-pointer transition-all duration-200 hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            outline: "border border-border bg-card shadow-none",
            interactive:
                "border-2 border-transparent bg-card shadow-md cursor-pointer transition-all duration-200 hover:border-primary hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            skeleton: "flex h-full flex-col gap-4 rounded-3xl p-5 bg-card shadow-xl",
            menuPanel: "overflow-hidden rounded-2xl border border-border/60 bg-background shadow-xl",
            menuColumn:
                "flex h-full min-w-0 flex-col rounded-xl border border-border/50 bg-muted/20 p-4 transition-colors",
            menuColumnCompact: "rounded-lg border border-border/60 bg-muted/25 p-3 transition-colors",
        },
        palette: {
            violet: "",
            amber: "",
            green: "",
            pink: "",
            teal: "",
        },
    },
    compoundVariants: [
        { variant: "menuColumn", palette: "violet", class: "hover:border-violet-200/80" },
        { variant: "menuColumn", palette: "amber", class: "hover:border-amber-200/80" },
        { variant: "menuColumn", palette: "green", class: "hover:border-green-200/80" },
        { variant: "menuColumn", palette: "pink", class: "hover:border-pink-200/80" },
        { variant: "menuColumn", palette: "teal", class: "hover:border-teal-200/80" },
        { variant: "menuColumnCompact", palette: "violet", class: "hover:border-violet-200/80" },
        { variant: "menuColumnCompact", palette: "amber", class: "hover:border-amber-200/80" },
        { variant: "menuColumnCompact", palette: "green", class: "hover:border-green-200/80" },
        { variant: "menuColumnCompact", palette: "pink", class: "hover:border-pink-200/80" },
        { variant: "menuColumnCompact", palette: "teal", class: "hover:border-teal-200/80" },
    ],
    defaultVariants: { variant: "default" },
});

const cardHeaderVariants = cva("flex p-6", {
    variants: {
        variant: {
            default: "flex-col space-y-1.5",
            row: "flex-row items-start justify-between",
            centered: "flex-col items-center space-y-1.5 pb-2 text-center",
            menuPanel:
                "flex-row items-center justify-between gap-4 space-y-0 border-b border-border/50 bg-muted/30 p-5",
        },
    },
    defaultVariants: { variant: "default" },
});

const cardContentVariants = cva("", {
    variants: {
        variant: {
            default: "p-6 pt-0",
            media: "mx-6 mb-6 aspect-[5/3] overflow-hidden rounded-2xl bg-card",
            menuPanel: "grid min-w-0 grid-cols-5 gap-3 p-3 [&>*]:min-w-0",
            menuColumn: "flex flex-1 flex-col gap-0.5 border-t border-border/40 p-0 pt-3",
            menuColumnCompact: "space-y-0.5 border-t border-border/40 p-0 pt-2",
        },
    },
    defaultVariants: { variant: "default" },
});

const cardFooterVariants = cva("flex p-6 pt-0", {
    variants: {
        variant: {
            default: "items-center",
            stack: "flex-col items-start gap-1.5",
        },
    },
    defaultVariants: { variant: "default" },
});

export interface CardProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {}
export interface CardHeaderProps
    extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardHeaderVariants> {}
export interface CardContentProps
    extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardContentVariants> {}
export interface CardFooterProps
    extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardFooterVariants> {}
export interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
    asChild?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(({ className, variant, palette, ...props }, ref) => (
    <div ref={ref} className={cn(cardVariants({ variant, palette }), className)} {...props} />
));
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(({ className, variant, ...props }, ref) => (
    <div ref={ref} className={cn(cardHeaderVariants({ variant }), className)} {...props} />
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
    ({ className, children, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : "h3";

        return (
            <Comp ref={ref} className={cn("text-2xl font-semibold leading-none tracking-tight", className)} {...props}>
                {children}
            </Comp>
        );
    },
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
    ({ className, ...props }, ref) => (
        <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
    ),
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(({ className, variant, ...props }, ref) => (
    <div ref={ref} className={cn(cardContentVariants({ variant }), className)} {...props} />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(({ className, variant, ...props }, ref) => (
    <div ref={ref} className={cn(cardFooterVariants({ variant }), className)} {...props} />
));
CardFooter.displayName = "CardFooter";

export { Card, cardVariants, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
